import { useState, useEffect } from 'react';

interface WebVitals {
  lcp: number | null;  // Largest Contentful Paint
  fid: number | null;  // First Input Delay
  cls: number | null;  // Cumulative Layout Shift
  fcp: number | null;  // First Contentful Paint
  ttfb: number | null; // Time to First Byte
}

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceEntry {
  name: string;
  duration: number;
  startTime: number;
  entryType: string;
}

export const PerformanceMetrics = () => {
  const [webVitals, setWebVitals] = useState<WebVitals>({
    lcp: null,
    fid: null,
    cls: null,
    fcp: null,
    ttfb: null,
  });
  const [memory, setMemory] = useState<MemoryInfo | null>(null);
  const [renderCount, setRenderCount] = useState(0);
  const [resources, setResources] = useState<PerformanceEntry[]>([]);
  const [bundleInfo, setBundleInfo] = useState<{ js: number; css: number; total: number } | null>(null);

  useEffect(() => {
    // Track render count
    setRenderCount(prev => prev + 1);

    // Get Web Vitals
    const getWebVitals = () => {
      try {
        // LCP
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          setWebVitals(prev => ({ ...prev, lcp: Math.round(lastEntry.startTime) }));
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

        // FCP
        const fcpEntries = performance.getEntriesByName('first-contentful-paint');
        if (fcpEntries.length > 0) {
          setWebVitals(prev => ({ ...prev, fcp: Math.round(fcpEntries[0].startTime) }));
        }

        // CLS
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!(entry as PerformanceEntry & { hadRecentInput?: boolean }).hadRecentInput) {
              clsValue += (entry as PerformanceEntry & { value?: number }).value || 0;
            }
          }
          setWebVitals(prev => ({ ...prev, cls: Math.round(clsValue * 1000) / 1000 }));
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });

        // TTFB
        const navEntries = performance.getEntriesByType('navigation');
        if (navEntries.length > 0) {
          const nav = navEntries[0] as PerformanceNavigationTiming;
          setWebVitals(prev => ({ ...prev, ttfb: Math.round(nav.responseStart) }));
        }

        return () => {
          lcpObserver.disconnect();
          clsObserver.disconnect();
        };
      } catch (e) {
        console.warn('PerformanceObserver not supported:', e);
      }
    };

    // Get Memory Info (Chrome only)
    const getMemory = () => {
      const perf = performance as Performance & { memory?: MemoryInfo };
      if (perf.memory) {
        setMemory({
          usedJSHeapSize: perf.memory.usedJSHeapSize,
          totalJSHeapSize: perf.memory.totalJSHeapSize,
          jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
        });
      }
    };

    // Get Resource Timing
    const getResources = () => {
      const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      const sorted = entries
        .map(e => ({
          name: e.name.split('/').pop() || e.name,
          duration: Math.round(e.duration),
          startTime: Math.round(e.startTime),
          entryType: e.initiatorType,
        }))
        .sort((a, b) => b.duration - a.duration)
        .slice(0, 10);
      setResources(sorted);

      // Calculate bundle sizes
      let jsSize = 0;
      let cssSize = 0;
      entries.forEach(e => {
        if (e.transferSize) {
          if (e.name.endsWith('.js') || e.initiatorType === 'script') {
            jsSize += e.transferSize;
          } else if (e.name.endsWith('.css') || e.initiatorType === 'css') {
            cssSize += e.transferSize;
          }
        }
      });
      setBundleInfo({
        js: Math.round(jsSize / 1024),
        css: Math.round(cssSize / 1024),
        total: Math.round((jsSize + cssSize) / 1024),
      });
    };

    getWebVitals();
    getMemory();
    getResources();

    // Update memory periodically
    const memInterval = setInterval(getMemory, 2000);
    return () => clearInterval(memInterval);
  }, []);

  const formatBytes = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)}MB`;
  };

  const getVitalRating = (metric: keyof WebVitals, value: number | null): { label: string; color: string } => {
    if (value === null) return { label: 'N/A', color: 'text-gray-500' };
    
    const thresholds: Record<string, [number, number]> = {
      lcp: [2500, 4000],   // Good < 2.5s, Needs Improvement < 4s
      fid: [100, 300],     // Good < 100ms
      cls: [0.1, 0.25],    // Good < 0.1
      fcp: [1800, 3000],   // Good < 1.8s
      ttfb: [800, 1800],   // Good < 800ms
    };
    
    const [good, poor] = thresholds[metric] || [0, 0];
    if (value <= good) return { label: 'Good', color: 'text-green-400' };
    if (value <= poor) return { label: 'Needs Work', color: 'text-yellow-400' };
    return { label: 'Poor', color: 'text-red-400' };
  };

  return (
    <div className="space-y-4">
      {/* Natural Language Summary */}
      <div className="bg-gradient-to-r from-green-900/50 to-teal-900/50 rounded-lg p-3 border border-green-500/30">
        <p className="text-sm">
          <span className="text-green-400 font-medium">Performance Summary:</span>{' '}
          <span className="text-gray-200">
            LCP: {webVitals.lcp ? (
              <span className={getVitalRating('lcp', webVitals.lcp).color}>{(webVitals.lcp / 1000).toFixed(1)}s ({getVitalRating('lcp', webVitals.lcp).label})</span>
            ) : 'measuring...'}, 
            {' '}{renderCount} renders this session
            {memory && <>, {formatBytes(memory.usedJSHeapSize)} memory used</>}
          </span>
        </p>
      </div>

      {/* Core Web Vitals */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-green-400 mb-2">📊 Core Web Vitals</h4>
        <div className="grid grid-cols-5 gap-2">
          {[
            { key: 'lcp', label: 'LCP', unit: 'ms', description: 'Largest Contentful Paint' },
            { key: 'fid', label: 'FID', unit: 'ms', description: 'First Input Delay' },
            { key: 'cls', label: 'CLS', unit: '', description: 'Cumulative Layout Shift' },
            { key: 'fcp', label: 'FCP', unit: 'ms', description: 'First Contentful Paint' },
            { key: 'ttfb', label: 'TTFB', unit: 'ms', description: 'Time to First Byte' },
          ].map(({ key, label, unit, description }) => {
            const value = webVitals[key as keyof WebVitals];
            const rating = getVitalRating(key as keyof WebVitals, value);
            return (
              <div key={key} className="bg-gray-900 rounded p-2 text-center" title={description}>
                <div className="text-xs text-gray-500">{label}</div>
                <div className={`text-lg font-bold ${rating.color}`}>
                  {value !== null ? (key === 'cls' ? value : value) : '—'}
                </div>
                <div className="text-[10px] text-gray-600">{unit}</div>
                <div className={`text-[10px] ${rating.color}`}>{rating.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Memory Usage */}
      {memory && (
        <div className="bg-gray-800 rounded-lg p-3">
          <h4 className="text-sm font-medium text-green-400 mb-2">🧠 Memory Usage</h4>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">JS Heap</span>
                <span className="text-gray-300">{formatBytes(memory.usedJSHeapSize)} / {formatBytes(memory.totalJSHeapSize)}</span>
              </div>
              <div className="h-2 bg-gray-900 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-green-500 to-green-400"
                  style={{ width: `${(memory.usedJSHeapSize / memory.totalJSHeapSize) * 100}%` }}
                />
              </div>
            </div>
            <div className="text-xs text-gray-500">
              Heap Limit: {formatBytes(memory.jsHeapSizeLimit)}
            </div>
          </div>
        </div>
      )}

      {/* Bundle Size */}
      {bundleInfo && (
        <div className="bg-gray-800 rounded-lg p-3">
          <h4 className="text-sm font-medium text-green-400 mb-2">📦 Bundle Size</h4>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-900 rounded p-2">
              <div className="text-xs text-gray-500">JavaScript</div>
              <div className="text-lg font-bold text-yellow-400">{bundleInfo.js}KB</div>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <div className="text-xs text-gray-500">CSS</div>
              <div className="text-lg font-bold text-blue-400">{bundleInfo.css}KB</div>
            </div>
            <div className="bg-gray-900 rounded p-2">
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-lg font-bold text-green-400">{bundleInfo.total}KB</div>
            </div>
          </div>
        </div>
      )}

      {/* Slowest Resources */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-green-400 mb-2">🐢 Slowest Resources</h4>
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {resources.map((r, i) => (
            <div key={i} className="flex items-center justify-between text-xs bg-gray-900 rounded p-1">
              <span className="text-gray-400 truncate flex-1" title={r.name}>{r.name}</span>
              <span className="text-gray-500 mx-2">{r.entryType}</span>
              <span className={`font-mono ${r.duration > 500 ? 'text-red-400' : r.duration > 200 ? 'text-yellow-400' : 'text-green-400'}`}>
                {r.duration}ms
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* React Renders */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-green-400 mb-2">⚛️ React Stats</h4>
        <div className="text-xs">
          <span className="text-gray-500">Component Renders:</span>
          <span className="ml-2 text-gray-300">{renderCount}</span>
        </div>
      </div>
    </div>
  );
};

